import { InputNumber, Select } from "antd";
import ZoomableImage from "components/ZoomableImage";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import { useSelector } from "react-redux";
import { absoluteTimeLookup, asyncLoadImage, getUrlDataForFrame, videoFrameExtractor, videoTimestampExtractor } from "../util/PlaylistUtils";

const dashboardDataSelector = (state) => {
    return _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state"], {});
};
const playlistSelector = (state) => state.playlist.data;
const imageKeysSelector = (state) => {
    return _.get(state.playlist.data, ["video"], []);
};
const baseUrlSelector = (state) => {
    return _.get(state.playlist.data, ["mpdURLs", `snapshots`]);
};
const streamInfoSelector = (state) => {
    const currentSession = _.get(state.sessions, [state.playlist.data.routeID], {});
    return _.get(currentSession, ["stream_info"], []);
};
const csrfTokenSelector = (state) => state.csrfToken;

const SidekickPlayer = ({ sourceIndex, setSourceIndex }) => {
    const dashboardData = useSelector(dashboardDataSelector);
    const playlist = useSelector(playlistSelector);
    const allImageKeys = useSelector(imageKeysSelector);
    const baseURL = useSelector(baseUrlSelector);
    const streamInfo = useSelector(streamInfoSelector);
    const csrfToken = useSelector(csrfTokenSelector);

    const [manualOffset, setManualOffset] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [imageData, setImageData] = useState(null);
    const [sourceList, setSourceList] = useState([]);

    const imageUrlToDisplay = useRef();

    const currentTimestamp = useMemo(() => {
        return dashboardData.position?.timestamp;
    }, [dashboardData.position]);

    const imageKeys = useMemo(() => {
        return _.get(allImageKeys, [sourceIndex], []);
    }, [allImageKeys, sourceIndex]);

    useEffect(() => {
        const sources = [];

        if (!playlist || !playlist.video) {
            setSourceList([]);
            return;
        }

        for (let i = 0; i < playlist.video.length; i++) {
            if (!playlist.video[i].length) {
                continue;
            }

            const sourceStreamData = _.get(streamInfo, [i], {});
            const sourceStreamInfo = _.get(sourceStreamData, ["info"], {});
            const archived = _.get(sourceStreamData, "archived", 0);
            if (archived === 1) {
                continue;
            }

            const sourceName = _.get(sourceStreamInfo, ["type"], "Camera " + (i + 1));
            const sourceLabel = _.get(sourceStreamInfo, ["label"], "");

            let name = sourceName;
            if (sourceLabel) {
                name += " (" + sourceLabel + ")";
            }

            const source = {
                name: name,
                streams: [
                    {
                        name: "Still Images",
                        videoIndex: i,
                        isVideo: true,
                        isEnhanced: false,
                        isStills: true,
                    },
                ],
            };

            sources.push(source);
        }

        setSourceIndex(_.get(sources, "[0].streams[0].videoIndex"));
        setSourceList(sources);
    }, [playlist, setSourceIndex, streamInfo]);

    useEffect(() => {
        if (!sourceList || !sourceList.length) {
            setImageData(null);
        }
    }, [sourceList]);

    const getUrlDataForImage = useCallback(
        (index, subIndex) => {
            if (index !== -1) {
                let selectedKey = imageKeys[index];
                if (selectedKey) {
                    let imageKey = selectedKey[0];

                    let imageFile = imageKey;
                    let range = null;

                    const urlFrameData = getUrlDataForFrame(imageKeys, index, subIndex);
                    imageFile = urlFrameData.imageFile;
                    range = urlFrameData.range;

                    let url = baseURL + imageFile;
                    if (csrfToken) {
                        url += `?csrf=${csrfToken}`;
                    }

                    return {
                        url,
                        range,
                        cacheKey: `${url}.${subIndex}`,
                    };
                }
            }
            return null;
        },
        [baseURL, csrfToken, imageKeys],
    );

    useEffect(() => {
        let indexToUse = absoluteTimeLookup(currentTimestamp / 1000, imageKeys);
        indexToUse = Math.min(Math.max(1, indexToUse), imageKeys.length - 1);

        const playlistItem = imageKeys[indexToUse];
        if (!playlistItem) {
            return;
        }
        let frameCount = videoFrameExtractor(playlistItem);

        const currentOffset = currentTimestamp / 1000 - videoTimestampExtractor(playlistItem);

        let subIndex = Math.ceil(currentOffset * frameCount) + manualOffset;
        if (subIndex > frameCount) {
            subIndex = subIndex % frameCount;
            indexToUse += 1;
        } else if (subIndex < 0) {
            subIndex = frameCount + subIndex;
            indexToUse -= 1;
        }

        subIndex = Math.min(Math.max(1, subIndex), frameCount - 1);

        const urlData = getUrlDataForImage(indexToUse, subIndex);

        if (!urlData) {
            return;
        }

        imageUrlToDisplay.current = urlData.cacheKey;
        asyncLoadImage(urlData.url, urlData.range, (imageData) => {
            if (urlData.cacheKey === imageUrlToDisplay.current) {
                setImageData(imageData);
            }
        });
    }, [currentTimestamp, getUrlDataForImage, imageKeys, manualOffset]);

    if (sourceList && !sourceList.length) {
        return (
            <div className="widget__Info center">
                <p>No video data available for this session</p>
            </div>
        );
    }

    return (
        <div className="SidekickPlayer">
            <div className="SidekickPlayer__Header">
                <div className="InputGroup">
                    <label>Offset:</label>
                    <InputNumber
                        size="small"
                        max={50}
                        min={-50}
                        value={manualOffset}
                        onChange={setManualOffset}
                    />
                </div>
                <div className="InputGroup">
                    <label>Type:</label>
                    <Select
                        size="small"
                        value={sourceIndex}
                        onChange={setSourceIndex}>
                        {sourceList.map((source) => (
                            <Select.Option
                                key={source.name}
                                value={_.get(source, "streams[0].videoIndex")}>
                                {source.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            <ZoomableImage
                imgSrc={imageData}
                zoom={zoom}
                updateZoom={(zoom) => setZoom(zoom)}
            />
        </div>
    );
};

export default SidekickPlayer;
